import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { orpc } from '@/api/rpc/client';
import type {
  ArSavedReport,
  CreateArSavedReportInput,
  UpdateArSavedReportInput,
  DeleteArSavedReportInput,
} from '@erp/rpc';

/**
 * Hook for listing all saved reports for the current user.
 * Ordered by most recently opened, then creation date.
 */
export function useArSavedReportsList() {
  const query = useQuery(
    orpc.accountsReceivable.arSavedReports.list.queryOptions({}),
  );
  return {
    ...query,
    reports: query.data?.reports ?? [],
  } satisfies typeof query & {
    reports: ArSavedReport[];
  };
}

/**
 * Hook for fetching a single saved report by id.
 * Disabled when id is null. Fetching updates lastOpenedAt server-side.
 */
export function useArSavedReport(id: string | null) {
  const query = useQuery({
    ...orpc.accountsReceivable.arSavedReports.get.queryOptions({
      input: { id: id ?? '' },
    }),
    enabled: id !== null,
  });
  return { ...query, report: query.data ?? null } satisfies typeof query & {
    report: ArSavedReport | null;
  };
}

/**
 * Hook for creating a new saved report.
 * Invalidates the list query on success.
 */
export function useCreateArSavedReport() {
  const queryClient = useQueryClient();
  return useMutation(
    orpc.accountsReceivable.arSavedReports.create.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries({
          queryKey: orpc.accountsReceivable.arSavedReports.list.key(),
        });
      },
    }),
  ) satisfies ReturnType<
    typeof useMutation<ArSavedReport, Error, CreateArSavedReportInput>
  >;
}

/**
 * Hook for updating an existing saved report (rename, re-save filters, set default).
 * Invalidates the list query on success.
 */
export function useUpdateArSavedReport() {
  const queryClient = useQueryClient();
  return useMutation(
    orpc.accountsReceivable.arSavedReports.update.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries({
          queryKey: orpc.accountsReceivable.arSavedReports.list.key(),
        });
      },
    }),
  ) satisfies ReturnType<
    typeof useMutation<ArSavedReport, Error, UpdateArSavedReportInput>
  >;
}

/**
 * Hook for permanently deleting a saved report.
 * Invalidates the list query on success.
 */
export function useDeleteArSavedReport() {
  const queryClient = useQueryClient();
  return useMutation(
    orpc.accountsReceivable.arSavedReports.delete.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries({
          queryKey: orpc.accountsReceivable.arSavedReports.list.key(),
        });
      },
    }),
  ) satisfies ReturnType<
    typeof useMutation<{ success: boolean }, Error, DeleteArSavedReportInput>
  >;
}
